import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import walletService from '../../services/walletService';

// --- Async Thunks ---

export const fetchBalance = createAsyncThunk(
  'wallet/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getBalance();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch balance');
    }
  }
);

export const fetchTransactions = createAsyncThunk(
  'wallet/fetchTransactions',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await walletService.getTransactions(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch transactions');
    }
  }
);

export const initiateRecharge = createAsyncThunk(
  'wallet/initiateRecharge',
  async ({ amount, currency = 'INR', idempotencyKey }, { rejectWithValue }) => {
    try {
      const response = await walletService.initiateRecharge(amount, currency, idempotencyKey);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to initiate recharge');
    }
  }
);

export const verifyRecharge = createAsyncThunk(
  'wallet/verifyRecharge',
  async ({ paymentData, idempotencyKey }, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.verifyRecharge(paymentData, idempotencyKey);
      // Refresh balance after successful recharge
      dispatch(fetchBalance());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Payment verification failed');
    }
  }
);

export const requestWithdrawal = createAsyncThunk(
  'wallet/requestWithdrawal',
  async ({ amount, bankAccountId, paymentMethodId, idempotencyKey }, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.requestWithdrawal(amount, bankAccountId, paymentMethodId, idempotencyKey);
      dispatch(fetchBalance());
      dispatch(fetchWithdrawals());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to submit withdrawal');
    }
  }
);

export const fetchWithdrawals = createAsyncThunk(
  'wallet/fetchWithdrawals',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getWithdrawals();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch withdrawals');
    }
  }
);

export const addBankAccount = createAsyncThunk(
  'wallet/addBankAccount',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.addBankAccount(data);
      dispatch(fetchBankAccounts());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to add bank account');
    }
  }
);

export const fetchBankAccounts = createAsyncThunk(
  'wallet/fetchBankAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getBankAccounts();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch bank accounts');
    }
  }
);

export const deleteBankAccount = createAsyncThunk(
  'wallet/deleteBankAccount',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await walletService.deleteBankAccount(id);
      dispatch(fetchBankAccounts());
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to delete bank account');
    }
  }
);

export const addPayoutUPI = createAsyncThunk(
  'wallet/addPayoutUPI',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.addPayoutUPI(data);
      dispatch(fetchPayoutUPIMethods());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to add UPI payout method');
    }
  }
);

export const fetchPayoutUPIMethods = createAsyncThunk(
  'wallet/fetchPayoutUPIMethods',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getPayoutUPIMethods();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch UPI payout methods');
    }
  }
);

export const fetchPendingWithdrawals = createAsyncThunk(
  'wallet/fetchPendingWithdrawals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await walletService.getPendingWithdrawals(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch pending withdrawals');
    }
  }
);

export const approveWithdrawal = createAsyncThunk(
  'wallet/approveWithdrawal',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.approveWithdrawal(id);
      dispatch(fetchPendingWithdrawals());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to approve withdrawal');
    }
  }
);

export const rejectWithdrawal = createAsyncThunk(
  'wallet/rejectWithdrawal',
  async ({ id, reason }, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.rejectWithdrawal(id, reason);
      dispatch(fetchPendingWithdrawals());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to reject withdrawal');
    }
  }
);

export const requestRefund = createAsyncThunk(
  'wallet/requestRefund',
  async ({ amount, reason, idempotencyKey }, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.requestRefund(amount, reason, idempotencyKey);
      dispatch(fetchBalance());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Refund request failed');
    }
  }
);

// --- Slice ---

const initialState = {
  balance: null,
  transactions: [],
  pagination: { nextCursor: null, hasMore: false, totalCount: 0 },
  withdrawals: [],
  bankAccounts: [],
  payoutUPIMethods: [],
  pendingWithdrawals: { requests: [], pagination: {} },
  loading: {
    balance: false,
    transactions: false,
    recharge: false,
    withdrawal: false,
    bankAccounts: false,
    payoutUPI: false,
    pendingWithdrawals: false,
    refund: false,
  },
  error: {
    balance: null,
    transactions: null,
    recharge: null,
    withdrawal: null,
    bankAccounts: null,
    payoutUPI: null,
    pendingWithdrawals: null,
    refund: null,
  },
};

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    updateBalanceFromSocket(state, action) {
      if (state.balance) {
        state.balance.available = action.payload.available ?? action.payload.balance;
        state.balance.frozen = action.payload.frozen ?? state.balance.frozen;
        state.balance.total = (state.balance.available || 0) + (state.balance.frozen || 0);
      }
    },
    clearWalletError(state, action) {
      const field = action.payload;
      if (field && state.error[field] !== undefined) {
        state.error[field] = null;
      }
    },
    resetWallet() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // fetchBalance
    builder
      .addCase(fetchBalance.pending, (state) => { state.loading.balance = true; state.error.balance = null; })
      .addCase(fetchBalance.fulfilled, (state, action) => { state.loading.balance = false; state.balance = action.payload; })
      .addCase(fetchBalance.rejected, (state, action) => { state.loading.balance = false; state.error.balance = action.payload; });

    // fetchTransactions
    builder
      .addCase(fetchTransactions.pending, (state) => { state.loading.transactions = true; state.error.transactions = null; })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading.transactions = false;
        const { transactions, pagination } = action.payload;
        if (action.meta.arg?.cursor) {
          state.transactions = [...state.transactions, ...(transactions || [])];
        } else {
          state.transactions = transactions || [];
        }
        state.pagination = {
          nextCursor: pagination?.next_cursor,
          hasMore: pagination?.has_more,
          totalCount: pagination?.total_count,
        };
      })
      .addCase(fetchTransactions.rejected, (state, action) => { state.loading.transactions = false; state.error.transactions = action.payload; });

    // initiateRecharge
    builder
      .addCase(initiateRecharge.pending, (state) => { state.loading.recharge = true; state.error.recharge = null; })
      .addCase(initiateRecharge.fulfilled, (state) => { state.loading.recharge = false; })
      .addCase(initiateRecharge.rejected, (state, action) => { state.loading.recharge = false; state.error.recharge = action.payload; });

    // verifyRecharge
    builder
      .addCase(verifyRecharge.pending, (state) => { state.loading.recharge = true; state.error.recharge = null; })
      .addCase(verifyRecharge.fulfilled, (state) => { state.loading.recharge = false; })
      .addCase(verifyRecharge.rejected, (state, action) => { state.loading.recharge = false; state.error.recharge = action.payload; });

    // requestWithdrawal
    builder
      .addCase(requestWithdrawal.pending, (state) => { state.loading.withdrawal = true; state.error.withdrawal = null; })
      .addCase(requestWithdrawal.fulfilled, (state) => { state.loading.withdrawal = false; })
      .addCase(requestWithdrawal.rejected, (state, action) => { state.loading.withdrawal = false; state.error.withdrawal = action.payload; });

    // fetchWithdrawals
    builder
      .addCase(fetchWithdrawals.fulfilled, (state, action) => { state.withdrawals = action.payload || []; });

    // fetchBankAccounts
    builder
      .addCase(fetchBankAccounts.pending, (state) => { state.loading.bankAccounts = true; })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => { state.loading.bankAccounts = false; state.bankAccounts = action.payload || []; })
      .addCase(fetchBankAccounts.rejected, (state, action) => { state.loading.bankAccounts = false; state.error.bankAccounts = action.payload; });

    // fetchPendingWithdrawals
    builder
      .addCase(fetchPendingWithdrawals.pending, (state) => { state.loading.pendingWithdrawals = true; })
      .addCase(fetchPendingWithdrawals.fulfilled, (state, action) => { state.loading.pendingWithdrawals = false; state.pendingWithdrawals = action.payload || { requests: [], pagination: {} }; })
      .addCase(fetchPendingWithdrawals.rejected, (state, action) => { state.loading.pendingWithdrawals = false; state.error.pendingWithdrawals = action.payload; });

    // requestRefund
    builder
      .addCase(requestRefund.pending, (state) => { state.loading.refund = true; state.error.refund = null; })
      .addCase(requestRefund.fulfilled, (state) => { state.loading.refund = false; })
      .addCase(requestRefund.rejected, (state, action) => { state.loading.refund = false; state.error.refund = action.payload; });

    // addPayoutUPI
    builder
      .addCase(addPayoutUPI.pending, (state) => { state.loading.payoutUPI = true; state.error.payoutUPI = null; })
      .addCase(addPayoutUPI.fulfilled, (state) => { state.loading.payoutUPI = false; })
      .addCase(addPayoutUPI.rejected, (state, action) => { state.loading.payoutUPI = false; state.error.payoutUPI = action.payload; });

    // fetchPayoutUPIMethods
    builder
      .addCase(fetchPayoutUPIMethods.pending, (state) => { state.loading.payoutUPI = true; })
      .addCase(fetchPayoutUPIMethods.fulfilled, (state, action) => { state.loading.payoutUPI = false; state.payoutUPIMethods = action.payload || []; })
      .addCase(fetchPayoutUPIMethods.rejected, (state, action) => { state.loading.payoutUPI = false; state.error.payoutUPI = action.payload; });
  },
});

export const { updateBalanceFromSocket, clearWalletError, resetWallet } = walletSlice.actions;
export default walletSlice.reducer;
